import { AssessmentResult } from '../../types/assessment';
import { ratingClass, displayRating, barColor, scoreClass } from './scoreHelpers';
import { computeScores } from './computeScores';
import TiCard from '../common/TiCard';

interface Props {
  result: AssessmentResult;
}

interface KnockoutRuleProps {
  label: string;
  description: string;
  triggered: boolean;
  tooltip?: string;
}

function KnockoutRule({ label, description, triggered, tooltip }: KnockoutRuleProps) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 10,
      padding: '8px 12px',
      borderRadius: 8,
      background: triggered ? '#FCEBEB' : '#f5f5f3',
      flex: '1 1 220px',
      minWidth: 0,
    }}>
      <span className={`badge ${triggered ? 'bg-red' : 'bg-green'}`} style={{ fontSize: 11, flexShrink: 0 }}>
        {triggered ? 'TRIGGERED' : 'CLEAR'}
      </span>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 500, color: triggered ? '#A32D2D' : '#1a1a18' }}>{label}</div>
        <div style={{ fontSize: 11, color: triggered ? '#A32D2D' : '#888780' }}>
          {description}
          {tooltip && (
            <span
              title={tooltip}
              aria-label={tooltip}
              style={{ marginLeft: 4, cursor: 'help', fontWeight: 600 }}
            >
              &#9432;
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ScoreSummaryCard({ result }: Props) {
  const { article, knockout, rating, is_opinion } = result;
  const rc = ratingClass(rating);
  const c = computeScores(result);

  // Recompute knockout flags from the canonical computed scores so each badge
  // can never contradict the number printed beside it (e.g. "Veracity 27 →
  // CLEAR"). Rule 3 (synthetic media) stays as the model judged it — it is not
  // derivable from the numeric scores.
  const rule1Triggered = c.manipulationTotal < 40;
  const rule2Triggered = (!is_opinion && c.qualityTotal < 40) || c.veracityArticle < 40;
  const rule3Triggered = knockout.rule3_triggered;

  // Compact score items for the breakdown row
  const scoreItems: { label: string; value: string; max: string; colorScore: number; colorMax: number }[] = [];
  if (!is_opinion) {
    scoreItems.push({ label: 'Quality', value: `${c.qualityTotal}`, max: '100', colorScore: c.qualityTotal, colorMax: 100 });
  }
  scoreItems.push({ label: 'Veracity', value: `${c.veracityFinal}`, max: '100', colorScore: c.veracityFinal, colorMax: 100 });
  if (!is_opinion) {
    scoreItems.push({ label: 'Emotional Balance', value: `${c.valenceTotal}`, max: '100', colorScore: c.valenceTotal, colorMax: 100 });
  }
  scoreItems.push({ label: 'Free of Manipulation', value: `${c.manipulationTotal}`, max: '100', colorScore: c.manipulationTotal, colorMax: 100 });

  return (
    <TiCard
      label="Assessment summary"
      footer={
        <>
          <div className="section-label" style={{ marginTop: 4 }}>Knockout criteria score</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <KnockoutRule
              label="Rule 1 — Manipulation"
              description={`Score ${c.manipulationTotal}/100 (threshold: 40)`}
              triggered={rule1Triggered}
            />
            <KnockoutRule
              label="Rule 2 — Accuracy"
              description={`Quality ${is_opinion ? 'N/A' : `${c.qualityTotal}/100`} · Veracity ${c.veracityArticle}/110 (article scoring only, threshold 40)`}
              tooltip="For the knockout check, only the article's own sourcing is scored — the publisher's reputation is not taken into account. (Publisher reputation is factored separately into the headline Veracity score.)"
              triggered={rule2Triggered}
            />
            <KnockoutRule
              label="Rule 3 — Synthetic media"
              description="AI/deepfake deception check"
              triggered={rule3Triggered}
            />
          </div>
          {knockout.note && (
            <div style={{ fontSize: 12, color: '#5f5e5a', marginTop: 8 }}>{knockout.note?.replace(/\bYELLOW\b/g, 'ORANGE')}</div>
          )}
          {is_opinion && result.opinion_note && (
            <div style={{ fontSize: 12, color: '#E06820', marginTop: 8 }}>
              OPINION/EDITORIAL — {result.opinion_note}
            </div>
          )}
        </>
      }
    >
      {/* Title + score */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14, gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 12, color: '#5f5e5a', marginBottom: 3 }}>
            {article.publisher}{article.author ? ` · ${article.author}` : ''}{article.date ? ` · ${article.date}` : ''} · <span className="badge bg-blue" style={{ fontSize: 10, padding: '2px 7px' }}>{article.content_type}</span>
          </div>
          <div style={{ fontSize: 15, fontWeight: 500, lineHeight: 1.4 }}>
            {article.url ? (
              <a
                href={article.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#1a73e8', textDecoration: 'none' }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.textDecoration = 'underline'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.textDecoration = 'none'; }}
              >
                {article.title}
              </a>
            ) : (
              <span style={{ color: '#1a1a18' }}>{article.title}</span>
            )}
          </div>
          {article.url && (
            <div style={{ fontSize: 11, color: '#5f5e5a', marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>
              <a
                href={article.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#5f5e5a', textDecoration: 'none' }}
              >
                {article.url}
              </a>
            </div>
          )}
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0, paddingLeft: 16 }}>
          <div style={{ fontSize: 38, fontWeight: 500, color: '#1a1a18', lineHeight: 1 }}>
            {c.finalScore}<span style={{ fontSize: 18, color: '#888780', fontWeight: 400 }}>/100</span>
          </div>
          <span className={`badge bg-${rc}`} style={{ marginTop: 6, fontSize: 12 }}>{displayRating(rating)}</span>
        </div>
      </div>

      {/* Progress bar */}
      <div className="bar-bg" style={{ margin: '0 0 14px 0', width: '100%', height: 10, borderRadius: 5 }}>
        <div className="bar-fill" style={{ width: `${Math.min(c.finalScore, 100)}%`, background: barColor(c.finalScore, 100), height: 10, borderRadius: 5 }} />
      </div>

      {/* Score breakdown */}
      <div style={{ background: '#f5f5f3', borderRadius: 8, padding: '12px 14px', marginTop: 4, marginBottom: 14 }}>
        {/* Index scores */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px', marginBottom: !is_opinion ? 10 : 0 }}>
          {scoreItems.map((item) => (
            <div key={item.label} style={{ display: 'flex', alignItems: 'baseline', gap: 6, minWidth: 0 }}>
              <span style={{ fontSize: 12, color: '#888780' }}>{item.label}</span>
              <span className={scoreClass(item.colorScore, item.colorMax)} style={{ fontSize: 14, fontWeight: 500 }}>{item.value}</span>
              <span style={{ fontSize: 11, color: '#b0afa8' }}>/ {item.max}</span>
            </div>
          ))}
        </div>

        {/* Weighted computation */}
        {!is_opinion && (
          <>
            <div style={{ borderTop: '0.5px solid rgba(0,0,0,0.1)', paddingTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 12, color: '#5f5e5a' }}>
                <span style={{ minWidth: 0 }}>Accuracy (Quality {c.qualityTotal} + Veracity {c.veracityFinal}) / 2 &times; 0.60</span>
                <span className={scoreClass(c.accuracyWeighted, 60)} style={{ fontWeight: 500, flexShrink: 0, whiteSpace: 'nowrap' }}>{c.accuracyWeighted.toFixed(1)} <span style={{ color: '#888780', fontWeight: 400 }}>/ 60</span></span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 12, color: '#5f5e5a' }}>
                <span style={{ minWidth: 0 }}>Emotional (Valence {c.valenceTotal} + Manipulation {c.manipulationTotal}) / 2 &times; 0.40</span>
                <span className={scoreClass(c.emotionalWeighted, 40)} style={{ fontWeight: 500, flexShrink: 0, whiteSpace: 'nowrap' }}>{c.emotionalWeighted.toFixed(1)} <span style={{ color: '#888780', fontWeight: 400 }}>/ 40</span></span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 12, color: '#1a1a18', fontWeight: 500, borderTop: '0.5px solid rgba(0,0,0,0.1)', paddingTop: 6, marginTop: 2 }}>
                <span style={{ minWidth: 0 }}>Final ({c.accuracyWeighted.toFixed(1)} + {c.emotionalWeighted.toFixed(1)})</span>
                <span className={`val-${rc}`} style={{ flexShrink: 0, whiteSpace: 'nowrap' }}>{c.finalScore} / 100</span>
              </div>
            </div>
          </>
        )}
      </div>
    </TiCard>
  );
}
